import { NotAMemberPage } from "@/pages/not-a-member";
import { useWorkspaceStore } from "@/store/workspace-store";
import { useEffect, useState } from "react";
import { Navigate, Outlet, useParams } from "react-router";
import { LoadingSpinner } from "../ui/loading-spinner";
import { WorkspaceLoadError } from "./workspace-load-error";

export function WorkspaceRouteWrapper() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const {
    workspaces,
    activeWorkspace,
    setActiveWorkspaceId,
    isInitialized,
    isInitializing,
    initError,
    initializeWorkspaces,
  } = useWorkspaceStore();
  const [isValidating, setIsValidating] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    console.log("🔍 WorkspaceRouteWrapper: useEffect triggered", {
      workspaceId,
      isInitialized,
      isInitializing,
      workspacesCount: workspaces.length,
    });

    if (!workspaceId) {
      console.log("🔍 WorkspaceRouteWrapper: Missing workspaceId");
      setIsValidating(false);
      return;
    }

    // After a failure, wait for the user to retry rather than looping.
    if (initError) {
      return;
    }

    if (!isInitialized && !isInitializing) {
      console.log(
        "🔍 WorkspaceRouteWrapper: Not initialized, initializing workspaces..."
      );
      initializeWorkspaces().catch((error) => {
        console.error(
          "❌ WorkspaceRouteWrapper: Failed to initialize workspaces:",
          error
        );
        setIsValidating(false);
      });
      return;
    }

    if (isInitializing) {
      console.log("🔍 WorkspaceRouteWrapper: Still initializing, waiting...");
      return;
    }

    // Check if user has access to this workspace
    const workspace = workspaces.find((w) => w._id === workspaceId);
    console.log("🔍 WorkspaceRouteWrapper: Found workspace:", workspace);

    if (workspace) {
      setHasAccess(true);
      // If this workspace is different from the active one, switch to it
      if (activeWorkspace?._id !== workspaceId) {
        console.log(
          "🔍 WorkspaceRouteWrapper: Switching to workspace:",
          workspaceId
        );
        setActiveWorkspaceId(workspaceId);
      }
    } else {
      console.log(
        "🔍 WorkspaceRouteWrapper: No access to workspace:",
        workspaceId
      );
      setHasAccess(false);
    }

    setIsValidating(false);
  }, [
    workspaceId,
    isInitialized,
    isInitializing,
    initError,
    // Remove workspaces, activeWorkspace, setActiveWorkspaceId, initializeWorkspaces from dependencies
    // to prevent infinite loops
  ]);

  if (initError) {
    return <WorkspaceLoadError />;
  }

  // Show loading while validating
  if (isValidating || !isInitialized || isInitializing) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  // No workspace ID provided - shouldn't happen with proper routing
  if (!workspaceId) {
    return <Navigate to="/" replace />;
  }

  // User doesn't have access to this workspace
  if (!hasAccess) {
    return <NotAMemberPage workspaceId={workspaceId} />;
  }

  // User has access - render the child routes
  return <Outlet />;
}
